import type { AIUseCase, AITool, BusinessProfile, RiskAssessment, RoleCheckRecord, TransparencyCheck } from "./types";
import { SCHEMA_VERSION } from "./types";

const redSignals = new Set([
  "sensitive_personal_data",
  "applicant_employee_data",
  "health_coaching_information",
  "health_or_psychological_diagnostics",
  "applicant_or_employee_evaluation",
  "scoring_or_prioritization_of_people",
  "automated_decision_about_people",
  "financial_credit_evaluation",
  "biometric_or_emotional_analysis",
  "minors",
  "ai_system_provided_to_clients",
  "customer_effective_output_without_human_review",
  "automatic_customer_reply",
  "automatic_without_review"
]);

const yellowSignals = new Set([
  "direct_customer_communication",
  "client_workflow_handover",
  "digital_product",
  "public_output",
  "public_content",
  "client_ready_text",
  "client_information",
  "confidential_client_data",
  "personal_data",
  "meeting_transcripts",
  "decision_support",
  "recommendation",
  "personal_data_possible",
  "direct_ai_interaction",
  "synthetic_media",
  "audio",
  "video",
  "weak_or_unknown_human_review",
  "uncertain_data_category"
]);

export function now() {
  return new Date().toISOString();
}

export function uid(prefix: string) {
  return `${prefix}_${crypto.randomUUID()}`;
}

export function evaluateUseCaseRisk(useCase: AIUseCase): RiskAssessment {
  const signals = [
    ...useCase.data_categories,
    useCase.context,
    useCase.output_type,
    useCase.automated_decision_about_people === "yes" ? "automated_decision_about_people" : "",
    useCase.customer_interaction === "yes" ? "direct_ai_interaction" : "",
    ["none", "rough", "automatic_without_review", "unknown", "no"].includes(useCase.human_review) ? "weak_or_unknown_human_review" : "",
    useCase.data_categories.includes("unknown") ? "uncertain_data_category" : ""
  ].filter(Boolean);
  const red = signals.filter((signal) => redSignals.has(signal));
  const yellow = useCaseYellowSignals(useCase, signals);
  const traffic_light = red.length ? "red" : yellow.length ? "yellow" : "green";
  return {
    id: `risk_use_case_${useCase.id}`,
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    source_type: "use_case",
    source_id: useCase.id,
    traffic_light,
    reasons: red.length ? red : yellow.length ? yellow : ["internal_or_low_risk_pattern"],
    positive_factors: ["yes", "always", "mostly"].includes(useCase.human_review) ? ["human_review_present"] : [],
    red_review_points: red
  };
}

function useCaseYellowSignals(useCase: AIUseCase, signals: string[]) {
  const yellow = signals.filter((signal) => yellowSignals.has(signal));
  const hasClientContext = useCase.context === "client_work";
  const hasPublicContext = useCase.context === "own_public_content";
  const hasLowConcernClientData = useCase.data_categories.some((category) => ["client_briefing_no_personal_data", "client_content_project_information"].includes(category));
  const hasMediaOutput = useCase.output_type === "image_graphic";
  const hasCustomerEffectiveOutput = ["client_ready_text", "public_content", "public_output", "recommendation", "decision_support"].includes(useCase.output_type);

  if (hasClientContext && hasCustomerEffectiveOutput) yellow.push("client_work");
  if (hasPublicContext && hasCustomerEffectiveOutput) yellow.push("own_public_content");
  if (hasLowConcernClientData && hasCustomerEffectiveOutput) yellow.push(useCase.data_categories.includes("client_content_project_information") ? "client_content_project_information" : "client_briefing_no_personal_data");
  if (hasMediaOutput && (hasClientContext || hasPublicContext || useCase.customer_interaction === "yes")) yellow.push("image_graphic");

  return [...new Set(yellow)];
}
export function evaluateToolRisk(tool: AITool): RiskAssessment {
  const red = tool.data_categories.filter((item) => redSignals.has(item));
  const yellow = tool.data_categories.filter((item) => yellowSignals.has(item));
  const traffic_light = red.length ? "red" : yellow.length ? "yellow" : "green";
  return {
    id: `risk_tool_${tool.id}`,
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    source_type: "tool",
    source_id: tool.id,
    traffic_light,
    reasons: red.length ? red : yellow.length ? yellow : ["tool_documented_with_low_risk_pattern"],
    positive_factors: ["yes", "always", "mostly"].includes(tool.human_review) ? ["human_review_present"] : [],
    red_review_points: red
  };
}

export function deriveRole(profile: BusinessProfile | undefined, useCases: AIUseCase[]): RoleCheckRecord {
  const hasProvidedAISystem = useCases.some((item) => item.context === "digital_product" || item.context === "client_workflow_handover" || item.data_categories.includes("ai_system_provided_to_clients"));
  const hasClientWorkflow = useCases.some((item) => item.context === "client_work" || item.context === "client_workflow_handover");
  const hasCustomerInteraction = useCases.some((item) => item.customer_interaction === "yes");
  const businessClientFacing = profile?.customer_types.some((type) => ["mostly_b2b", "mostly_b2c", "mixed_b2b_b2c", "public_audience", "clients"].includes(type)) ?? false;
  const role = hasProvidedAISystem || hasCustomerInteraction ? "D" : hasClientWorkflow ? "C" : businessClientFacing ? "B" : "A";
  return {
    id: uid("role"),
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    profile: role,
    signals: [hasProvidedAISystem && "ai_system_provided_to_clients", hasClientWorkflow && "client_work", hasCustomerInteraction && "direct_ai_interaction", businessClientFacing && "customer_facing_business"].filter(Boolean) as string[],
    requires_external_review: role === "C" || role === "D"
  };
}

export function deriveTransparency(useCases: AIUseCase[]): TransparencyCheck {
  const customer = useCases.some((item) => ["client_work", "direct_customer_communication", "client_workflow_handover"].includes(item.context) || item.customer_interaction === "yes");
  const publicOutput = useCases.some((item) => ["public_output", "public_content", "synthetic_media", "image_graphic", "audio", "video", "automatic_customer_reply"].includes(item.output_type));
  const direct = useCases.some((item) => item.customer_interaction === "yes");
  const red = useCases.some((item) => item.data_categories.some((category) => redSignals.has(category)));
  return {
    id: uid("transparency"),
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    customer_facing: customer ? "yes" : "no",
    public_output: publicOutput ? "yes" : "no",
    direct_ai_interaction: direct ? "yes" : "no",
    status: red ? "external_review_recommended" : customer || publicOutput || direct ? "notice_recommended" : "none_needed",
    recommended_notices: [
      customer && "Kunden-/Angebotshinweis",
      publicOutput && "Content- oder Website-Hinweis",
      direct && "Chatbot-/Interaktionshinweis"
    ].filter(Boolean) as string[]
  };
}
